import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventResponse, OrderResponse, OrderLineResponse } from '../../../../../../../../../../@shared/models/response.module';
import { TranslateService } from '@ngx-translate/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-view-order-details',
  standalone: false,
  templateUrl: './view-order-details.html',
  styleUrl: './view-order-details.css'
})
export class ViewOrderDetails implements OnInit {
  @Input() order: OrderResponse;
  @Input() event: EventResponse;

  generatingPdf: boolean = false;

  constructor(
    private activeModal: NgbActiveModal,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    console.log('Order details:', this.order);
  }

  close() {
    this.activeModal.dismiss();
  }

  formatCents(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  formatDateTime(dateStr: string | Date | null | undefined): string {
    if (!dateStr) return '-';
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'paid':
        return 'badge-light-success';
      case 'pending':
        return 'badge-light-warning';
      case 'failed':
        return 'badge-light-danger';
      case 'refunded':
        return 'badge-light-info';
      case 'cancelled':
        return 'badge-light-secondary';
      default:
        return 'badge-light-secondary';
    }
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      'paid': 'manageEvent.orders.statusPaid',
      'pending': 'manageEvent.orders.statusPending',
      'failed': 'manageEvent.orders.statusFailed',
      'refunded': 'manageEvent.orders.statusRefunded',
      'cancelled': 'manageEvent.orders.statusCancelled'
    };
    return this.translateService.instant(statusMap[status] || 'manageEvent.orders.statusPending');
  }

  getLineTypeLabel(lineType: string): string {
    switch (lineType) {
      case 'class_registration':
        return this.translateService.instant('manageEvent.orderDetails.typeClassRegistration');
      case 'service_registration':
        return this.translateService.instant('manageEvent.orderDetails.typeServiceRegistration');
      default:
        return lineType;
    }
  }

  getLineTypeIcon(lineType: string): string {
    switch (lineType) {
      case 'class_registration':
        return 'fa-list';
      case 'service_registration':
        return 'fa-concierge-bell';
      default:
        return 'fa-tag';
    }
  }

  getUserFullName(): string {
    if (this.order.user) {
      return `${this.order.user.first_name || ''} ${this.order.user.last_name || ''}`.trim() || 'Unknown';
    }
    return 'Unknown';
  }

  getPlatformFeePercentage(): string {
    if (this.order.subtotal_cents > 0) {
      const percentage = (this.order.platform_fee_cents / this.order.subtotal_cents) * 100;
      return percentage.toFixed(1);
    }
    return '0';
  }

  getSubtotalExVat(): number {
    const totalVat = this.order.total_vat_cents || 0;
    return this.order.subtotal_cents - totalVat;
  }

  getVatBreakdown(): { rate: number; amount: number }[] {
    if (!this.order.order_lines) return [];

    const vatMap = new Map<number, number>();
    for (const line of this.order.order_lines) {
      const rate = line.vat_rate || 0;
      const vatCents = line.vat_cents || 0;
      if (rate > 0 && vatCents > 0) {
        vatMap.set(rate, (vatMap.get(rate) || 0) + vatCents);
      }
    }

    return Array.from(vatMap.entries())
      .map(([rate, amount]) => ({ rate, amount }))
      .sort((a, b) => a.rate - b.rate);
  }

  downloadReceipt() {
    this.generatingPdf = true;

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Colors
      const primaryColor: [number, number, number] = [0, 123, 255];
      const successColor: [number, number, number] = [40, 167, 69];
      const warningColor: [number, number, number] = [255, 193, 7];
      const grayColor: [number, number, number] = [108, 117, 125];
      const lightGray: [number, number, number] = [248, 249, 250];
      const darkText: [number, number, number] = [33, 37, 41];

      // Header background
      doc.setFillColor(...lightGray);
      doc.rect(0, 0, pageWidth, 50, 'F');

      // Header - Event name
      yPos = 15;
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      const headerText = this.event?.event_name || 'Receipt';
      doc.text(headerText, margin, yPos);

      // Order number and status below event name
      yPos += 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text(`Order #${this.order.order_id}`, margin, yPos);

      // Status badge next to order number
      const statusText = this.getStatusLabel(this.order.status).toUpperCase();
      let statusColor = grayColor;
      if (this.order.status === 'paid') statusColor = successColor;
      else if (this.order.status === 'pending') statusColor = warningColor;

      doc.setTextColor(...statusColor);
      doc.text(`  ${statusText}`, margin + doc.getTextWidth(`Order #${this.order.order_id}`), yPos);

      // Date below order number
      yPos += 8;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayColor);
      doc.text(this.formatDateTime(this.order.created_at), margin, yPos);

      // Receipt title
      yPos = 60;
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('RECEIPT', pageWidth / 2, yPos, { align: 'center' });

      yPos += 20;

      // Two column layout for Customer and Event info
      const colWidth = (contentWidth - 10) / 2;
      const col1X = margin;
      const col2X = margin + colWidth + 10;

      // Customer Info Box
      doc.setFillColor(...lightGray);
      doc.roundedRect(col1X, yPos, colWidth, 45, 3, 3, 'F');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...grayColor);
      doc.text('CUSTOMER', col1X + 8, yPos + 10);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text(this.getUserFullName(), col1X + 8, yPos + 20);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayColor);
      if (this.order.user?.email) {
        doc.text(this.order.user.email, col1X + 8, yPos + 28);
      }
      if (this.order.user?.phone) {
        doc.text(this.order.user.phone, col1X + 8, yPos + 36);
      }

      // Event/Transaction Info Box
      doc.setFillColor(...lightGray);
      doc.roundedRect(col2X, yPos, colWidth, 45, 3, 3, 'F');

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...grayColor);
      doc.text('EVENT DETAILS', col2X + 8, yPos + 10);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      if (this.event) {
        const eventName = this.event.event_name || 'N/A';
        const truncatedName = eventName.length > 25 ? eventName.substring(0, 25) + '...' : eventName;
        doc.text(truncatedName, col2X + 8, yPos + 20);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...grayColor);
        if (this.event.start_date) {
          doc.text(this.formatDateTime(this.event.start_date), col2X + 8, yPos + 28);
        }
        if (this.event.city || this.event.country) {
          const location = [this.event.city, this.event.country].filter(Boolean).join(', ');
          doc.text(location, col2X + 8, yPos + 36);
        }
      }

      yPos += 60;

      // Order Items Section Title
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('ORDER ITEMS', margin, yPos);
      yPos += 8;

      // Order Items Table
      if (this.order.order_lines && this.order.order_lines.length > 0) {
        const tableData = this.order.order_lines.map(line => [
          line.description,
          line.quantity.toString(),
          `${this.formatCents(line.unit_price_cents)} ${this.order.currency}`,
          `${line.vat_rate || 0}%`,
          `${this.formatCents(line.vat_cents || 0)} ${this.order.currency}`,
          `${this.formatCents(line.total_cents)} ${this.order.currency}`
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Item', 'Qty', 'Price', 'MVA %', 'MVA', 'Total']],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: [52, 58, 64],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 9,
            cellPadding: 5,
            halign: 'left'
          },
          bodyStyles: {
            textColor: darkText,
            fontSize: 9,
            cellPadding: 5,
            lineColor: [230, 230, 230],
            lineWidth: 0.3
          },
          alternateRowStyles: {
            fillColor: [249, 250, 251]
          },
          columnStyles: {
            0: { cellWidth: 'auto', halign: 'left' },
            1: { cellWidth: 18, halign: 'center' },
            2: { cellWidth: 32, halign: 'right' },
            3: { cellWidth: 22, halign: 'right' },
            4: { cellWidth: 28, halign: 'right' },
            5: { cellWidth: 32, halign: 'right', fontStyle: 'bold' }
          },
          margin: { left: margin, right: margin },
          tableLineColor: [220, 220, 220],
          tableLineWidth: 0.3
        });

        yPos = (doc as any).lastAutoTable.finalY + 20;
      }

      // Order Summary - Right aligned box
      const summaryWidth = 100;
      const summaryX = pageWidth - margin - summaryWidth;
      const vatBreakdown = this.getVatBreakdown();
      const summaryHeight = 80 + (vatBreakdown.length * 10);

      doc.setFillColor(...lightGray);
      doc.roundedRect(summaryX, yPos, summaryWidth, summaryHeight, 3, 3, 'F');

      const labelX = summaryX + 8;
      const valueX = summaryX + summaryWidth - 8;

      yPos += 12;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayColor);
      doc.text('Sum eks. MVA', labelX, yPos);
      doc.setTextColor(...darkText);
      doc.text(`${this.formatCents(this.getSubtotalExVat())} ${this.order.currency}`, valueX, yPos, { align: 'right' });

      // VAT breakdown by rate
      for (const vatGroup of vatBreakdown) {
        yPos += 10;
        doc.setTextColor(...grayColor);
        doc.text(`MVA ${vatGroup.rate}%`, labelX, yPos);
        doc.setTextColor(...darkText);
        doc.text(`${this.formatCents(vatGroup.amount)} ${this.order.currency}`, valueX, yPos, { align: 'right' });
      }

      yPos += 10;
      doc.setTextColor(...grayColor);
      doc.text('Total MVA', labelX, yPos);
      doc.setTextColor(...darkText);
      doc.text(`${this.formatCents(this.order.total_vat_cents || 0)} ${this.order.currency}`, valueX, yPos, { align: 'right' });

      yPos += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(labelX, yPos, valueX, yPos);

      yPos += 10;
      doc.setTextColor(...grayColor);
      doc.text('Subtotal (inkl. MVA)', labelX, yPos);
      doc.setTextColor(...darkText);
      doc.text(`${this.formatCents(this.order.subtotal_cents)} ${this.order.currency}`, valueX, yPos, { align: 'right' });

      yPos += 10;
      doc.setTextColor(...grayColor);
      doc.text('Platform Fee', labelX, yPos);
      doc.setTextColor(...darkText);
      doc.text(`${this.formatCents(this.order.platform_fee_cents)} ${this.order.currency}`, valueX, yPos, { align: 'right' });

      yPos += 5;
      doc.setDrawColor(200, 200, 200);
      doc.line(labelX, yPos, valueX, yPos);

      yPos += 10;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkText);
      doc.text('Total', labelX, yPos);
      doc.text(`${this.formatCents(this.order.total_cents)} ${this.order.currency}`, valueX, yPos, { align: 'right' });

      // Footer
      yPos = pageHeight - 25;
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, yPos - 10, pageWidth - margin, yPos - 10);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...grayColor);
      doc.text('Thank you for your registration!', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.setFontSize(8);
      doc.text(`Generated on ${this.formatDateTime(new Date())}`, pageWidth / 2, yPos, { align: 'center' });

      // Save the PDF
      const filename = `receipt-order-${this.order.order_id}.pdf`;
      doc.save(filename);

    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      this.generatingPdf = false;
    }
  }
}
